import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { boloService } from '@inferium/shared/src/api';
import { PublicBolo } from '@inferium/shared/src/types';
import { fetchWithS3Fallback } from '@inferium/shared/src/utils/s3Fallback';
import './PublicBoloPage.css';

const PublicBoloPage: React.FC = () => {
  const { token } = useParams<{ companySlug: string; clientSlug: string; siteSlug: string; token: string }>();
  const [data, setData] = useState<PublicBolo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await fetchWithS3Fallback(
          () => boloService.getPublicBolo(token),
          `public-bolo/${token}.json`
        );
        setData(result);
      } catch (err) {
        setError('BOLO list not found or could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bolo-page">
        <div className="bolo-container">
          <div className="bolo-loading">Loading BOLO list...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bolo-page">
        <div className="bolo-container">
          <div className="bolo-error">{error || 'An error occurred.'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bolo-page">
      <div className="bolo-container">
        {/* Header */}
        <header className="bolo-header">
          <h1 className="bolo-title">Be On The Lookout</h1>
          <div className="bolo-meta">
            <span className="bolo-company-name">{data.companyName}</span>
            <span className="bolo-meta-separator">|</span>
            <span>{data.clientName}</span>
          </div>
          <div className="bolo-site-info">
            <strong>{data.siteName}</strong>
            <br />
            {data.siteAddress}
          </div>
          {data.companyLogo && (
            <div className="bolo-header-bottom">
              <img
                src={data.companyLogo}
                alt={data.companyName}
                className="bolo-company-logo"
              />
            </div>
          )}
        </header>

        {/* BOLO Items */}
        {data.items.length === 0 ? (
          <div className="bolo-empty">
            No individuals are currently on the lookout list for this site.
          </div>
        ) : (
          <div className="bolo-grid">
            {data.items.map((item, index) => (
              <div key={index} className="bolo-card">
                {item.imageUrl && (
                  <div
                    className="bolo-card-image-container"
                    onClick={() => setLightboxImage(item.imageUrl!)}
                  >
                    <img
                      src={item.imageUrl}
                      alt={item.name || 'BOLO Individual'}
                      className="bolo-card-image"
                    />
                  </div>
                )}
                <div className="bolo-card-content">
                  {item.name && (
                    <h3 className="bolo-card-name">{item.name}</h3>
                  )}
                  <p className="bolo-card-description">{item.description}</p>
                  <div className="bolo-card-meta">
                    Added by {item.createdByGuardName} on {formatDate(item.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <footer className="bolo-footer">
          <p>Powered by Inferium Reports</p>
        </footer>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div className="bolo-lightbox" onClick={() => setLightboxImage(null)}>
          <div className="bolo-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="bolo-lightbox-close" onClick={() => setLightboxImage(null)}>
              &times;
            </button>
            <img src={lightboxImage} alt="BOLO" className="bolo-lightbox-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicBoloPage;
